interface RetryOptions {
  retries?: number;
  delayMs?: number;
  label?: string;
  onRetry?: (error: unknown, attempt: number) => Promise<void> | void;
}

/**
 * Executes an asynchronous function with automatic retry logic.
 *
 * @param fn The asynchronous function to execute.
 * @param options Configuration options for retry behavior.
 * @param options.retries The maximum number of retry attempts (default: 3).
 * @param options.delayMs The delay in milliseconds between retries (default: 1000).
 * @param options.label A label used in log messages to identify the operation (default: 'operation').
 * @param options.onRetry Optional callback invoked before each retry, receiving the error and current attempt number.
 * @param attempt The current attempt number (used internally; defaults to 1).
 * @returns A promise that resolves with the result of the function, or rejects after all retries fail.
 * @throws An error if the function fails after the specified number of retries.
 */
export async function withRetry<T>(
  fn: () => Promise<T>,
  { retries = 3, delayMs = 1000, label = 'operation', onRetry }: RetryOptions = {},
  attempt = 1
): Promise<T> {
  try {
    const response = await fn();
    return response;
  } catch (err) {
    if (attempt < retries) {
      if (onRetry) {
        await onRetry(err, attempt);
      }

      await new Promise((res) => setTimeout(res, delayMs));

      return withRetry(fn, { retries, delayMs, label, onRetry }, attempt + 1);
    }

    throw new Error(
      `[withRetry] ${label} failed after ${retries} retries. Last error: ${(err as Error)?.message}`
    );
  }
}
